import { onBeforeUnmount, onMounted } from 'vue'

const LAYER_SELECTOR = '.app-bg__layer'
// Per-layer drift coefficients (front-most layer drifts most). Applied to
// scrollY and clamped, so the effect is a subtle depth cue, not 1:1 movement.
const PARALLAX_FACTORS = [0.1, 0.075, 0.05, 0.03]
// Keep the travel under .app-bg's --app-bg-overscan (80px) so a translated
// layer never exposes a hard edge over the solid body color.
const MAX_DRIFT_PX = 72
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export function useBackgroundParallax() {
  let reducedMotion = false
  let mq: MediaQueryList | undefined
  let ticking = false
  let lastScrollY = -1
  let layers: HTMLElement[] = []

  function collectLayers() {
    layers = Array.from(document.querySelectorAll<HTMLElement>(LAYER_SELECTOR))
  }

  function update() {
    ticking = false

    const scrollY = window.scrollY
    if (scrollY === lastScrollY) return
    lastScrollY = scrollY

    for (let i = 0; i < layers.length; i++) {
      const factor = PARALLAX_FACTORS[i] ?? 0
      const offset = clamp(scrollY * factor, -MAX_DRIFT_PX, MAX_DRIFT_PX)
      // transform/opacity are the only properties the compositor can animate
      // without repainting the (expensive) gradients.
      layers[i].style.transform = `translate3d(0, ${offset}px, 0)`
    }
  }

  function onScroll() {
    if (ticking) return
    ticking = true
    requestAnimationFrame(update)
  }

  function start() {
    if (reducedMotion) return
    collectLayers()
    window.addEventListener('scroll', onScroll, { passive: true })
    update()
  }

  function stop() {
    window.removeEventListener('scroll', onScroll)
    ticking = false
    lastScrollY = -1
  }

  function resetTransforms() {
    for (const layer of layers) {
      layer.style.removeProperty('transform')
    }
  }

  function onMotionChange(e: MediaQueryListEvent) {
    reducedMotion = e.matches
    if (reducedMotion) {
      stop()
      resetTransforms()
    } else {
      start()
    }
  }

  onMounted(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return

    mq = window.matchMedia(REDUCED_MOTION_QUERY)
    reducedMotion = mq.matches
    mq.addEventListener('change', onMotionChange)

    if (!reducedMotion) {
      start()
    }
  })

  onBeforeUnmount(() => {
    stop()
    if (mq) {
      mq.removeEventListener('change', onMotionChange)
    }
    resetTransforms()
  })
}
